"use client";

import React, { useState } from "react";
import { Activity, CircleAlert as AlertCircle, ArrowRight, BatteryCharging, CircleCheck as CheckCircle2, Cpu, Download, Flame, Layers, Radio, Settings2, ShieldCheck, Usb, Wifi } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
const variantsData = [{
  id: "onsite-w",
  name: "MicroNIR OnSite-W",
  tagline: "Handheld & Mobile Raw Material ID",
  category: "Handheld / Field NIR",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_onsite_w_spectrometer.png-wvc-sizes",
  imageAlt: "MicroNIR OnSite-W handheld wireless NIR spectrometer",
  connectivity: "Wireless (Bluetooth / Wi-Fi)",
  environment: "IP65 / IP67 Rugged Field & Warehouse",
  power: "Internal Rechargeable Li-ion (>8 hrs)",
  weight: "~250 g lightweight enclosure",
  spectralRange: "950 – 1650 nm",
  pixelResolution: "128-pixel linear InGaAs array",
  primaryUse: "Warehouse receiving, incoming raw materials, field inspection, agricultural testing",
  highlightBadge: "Handheld Mobility",
  statusReview: "Specifications marked for final technical sign-off"
}, {
  id: "pat-w",
  name: "MicroNIR PAT-W",
  tagline: "Wireless Real-Time Process Monitoring",
  category: "Process Analytical Technology",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_wireless_analyzer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_wireless_analyzer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_w_wireless_analyzer.png-wvc-sizes",
  imageAlt: "MicroNIR PAT-W wireless process NIR spectrometer for tumble blenders",
  connectivity: "Integrated Wi-Fi / Industrial Wireless",
  environment: "IP67 Washdown / Sanitary Tri-Clamp Mount",
  power: "Hot-swappable dual battery packs",
  weight: "~1.1 kg stainless steel housing",
  spectralRange: "950 – 1650 nm",
  pixelResolution: "128-pixel linear InGaAs array",
  primaryUse: "Rotating tumble blenders, bin blenders, fluid bed dryers, granulators",
  highlightBadge: "Wireless Process",
  statusReview: "Specifications marked for final technical sign-off"
}, {
  id: "pat-wx",
  name: "MicroNIR PAT-Wx",
  tagline: "ATEX / Hazardous Zone Wireless PAT",
  category: "Hazardous Locations / Ex",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_atex_spectrometer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_atex_spectrometer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_wx_atex_spectrometer.png-wvc-sizes",
  imageAlt: "MicroNIR PAT-Wx ATEX explosion-proof wireless NIR spectrometer",
  connectivity: "Hazardous area certified Wireless",
  environment: "ATEX / IECEx Zone 0/20 & Class I/II Div 1",
  power: "Ex-certified intrinsically safe battery",
  weight: "~1.4 kg reinforced 316L SS housing",
  spectralRange: "950 – 1650 nm",
  pixelResolution: "128-pixel linear InGaAs array",
  primaryUse: "Explosive solvent blend monitoring, chemical syntheses, volatile active drying",
  highlightBadge: "ATEX Certified",
  statusReview: "Specifications marked for final technical sign-off"
}, {
  id: "pat-u",
  name: "MicroNIR PAT-U",
  tagline: "Hardwired Continuous Inline Spectrometer",
  category: "Direct In-Line / Continuous",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_inline_spectrometer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_inline_spectrometer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_u_inline_spectrometer.png-wvc-sizes",
  imageAlt: "MicroNIR PAT-U hardwired inline process NIR analyzer",
  connectivity: "Direct USB 2.0 / Industrial Ethernet gateway",
  environment: "IP67 Stainless steel (CIP/SIP compatible)",
  power: "Direct bus powered (USB / 24V DC auxiliary)",
  weight: "~950 g compact stainless housing",
  spectralRange: "950 – 1650 nm",
  pixelResolution: "128-pixel linear InGaAs array",
  primaryUse: "Continuous manufacturing lines, feed frame monitoring, extrusion, tablet presses",
  highlightBadge: "Continuous Inline",
  statusReview: "Specifications marked for final technical sign-off"
}, {
  id: "pat-ux",
  name: "MicroNIR PAT-Ux",
  tagline: "Hardwired ATEX / Ex Continuous Process NIR",
  category: "Hazardous Locations / Ex Inline",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_ux_atex_inline_spectrometer.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_ux_atex_inline_spectrometer.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_pat_ux_atex_inline_spectrometer.png-wvc-sizes",
  imageAlt: "MicroNIR PAT-Ux ATEX rated hardwired continuous NIR spectrometer",
  connectivity: "Explosion-proof wired barrier conduit / Ex-d",
  environment: "ATEX / IECEx Zone 1/21 certified inline",
  power: "24V DC intrinsically safe barrier supply",
  weight: "~1.6 kg heavy-duty 316L SS",
  spectralRange: "950 – 1650 nm",
  pixelResolution: "128-pixel linear InGaAs array",
  primaryUse: "Hazardous inline feed frames, continuous solvent extraction, flammable powder transfer",
  highlightBadge: "ATEX Hardwired",
  statusReview: "Specifications marked for final technical sign-off"
}];
const technicalMatrix = [{
  feature: "Optics Engine",
  onsiteW: "Linear Variable Filter (LVF)",
  patW: "Linear Variable Filter (LVF)",
  patWx: "Linear Variable Filter (LVF)",
  patU: "Linear Variable Filter (LVF)",
  patUx: "Linear Variable Filter (LVF)"
}, {
  feature: "Wavelength Range",
  onsiteW: "950 – 1650 nm (Placeholder)",
  patW: "950 – 1650 nm (Placeholder)",
  patWx: "950 – 1650 nm (Placeholder)",
  patU: "950 – 1650 nm (Placeholder)",
  patUx: "950 – 1650 nm (Placeholder)"
}, {
  feature: "Communication",
  onsiteW: "Wireless / Bluetooth",
  patW: "Industrial Wi-Fi",
  patWx: "ATEX Ex-i Wireless",
  patU: "USB 2.0 / Modbus",
  patUx: "ATEX Conduit Hardwired"
}, {
  feature: "Ingress & Ex Rating",
  onsiteW: "IP65 / IP67",
  patW: "IP67 Washdown",
  patWx: "ATEX Zone 0/20 & Cl I/II",
  patU: "IP67 Sanitary",
  patUx: "ATEX Zone 1/21 & Cl I/II"
}, {
  feature: "Power Architecture",
  onsiteW: "Internal Li-ion Pack",
  patW: "Swappable Battery",
  patWx: "Ex-Certified Battery",
  patU: "Bus Powered / 24V DC",
  patUx: "24V DC IS Barrier"
}, {
  feature: "Primary Application",
  onsiteW: "Raw Material ID / Handheld",
  patW: "Tumble Blenders / PAT",
  patWx: "Hazardous Blend Monitoring",
  patU: "Continuous Extrusion / Lines",
  patUx: "Continuous Ex-rated Lines"
}];
export default function ProductVariantsGrid() {
  const shouldReduceMotion = useReducedMotion();
  const [selectedVariant, setSelectedVariant] = useState<string>("pat-w");
  return <section id="productvariantsgrid" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 border-b border-border">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider mb-4">
            <Cpu className="size-3.5 text-primary" />
            <span>Hardware Family & Deployment Options</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight mb-4">
            MicroNIR Instrument Configurations
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            From mobile raw material verification at the loading dock to ATEX-rated
            continuous manufacturing lines, explore the MicroNIR series engineered
            for regulated pharmaceutical and manufacturing suites across Ireland and the UK.
          </p>

          {/* Technical Review Notice */}
          <div className="mt-6 flex items-start gap-3 p-3.5 rounded-lg bg-accent/60 border border-border text-xs sm:text-sm text-accent-foreground">
            <AlertCircle className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <span className="font-semibold text-foreground">
                Technical Specification Notice:
              </span>{" "}
              Specifications below reflect preliminary configuration templates and are currently
              marked for formal technical verification prior to validation package issue.
            </div>
          </div>
        </div>

        {/* Product Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {variantsData.map((item, i) => {
          const isSelected = selectedVariant === item.id;
          return <motion.div key={item.id} data-index={i} initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.08
          }} className="flex">
                <Card className={`flex flex-col justify-between w-full border transition-[border-color,box-shadow] duration-200 bg-card ${isSelected ? "border-primary ring-1 ring-primary shadow-md" : "border-border hover:border-border/80 shadow-xs hover:shadow-sm"}`} onClick={() => setSelectedVariant(item.id)}>
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <Badge variant="secondary" className="text-xs font-medium text-secondary-foreground bg-secondary">
                        {item.highlightBadge}
                      </Badge>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="inline-flex items-center gap-1 text-2xs text-muted-foreground font-mono bg-muted/60 px-2 py-0.5 rounded cursor-help">
                              <ShieldCheck className="size-3 text-primary" />
                              Review Status
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className="bg-popover text-popover-foreground border border-border shadow-md">
                            <p>{item.statusReview}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                    <CardTitle className="text-xl font-default font-bold text-card-foreground">
                      {item.name}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-muted-foreground">
                      {item.tagline}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="space-y-4 flex-1">
                    {/* Visual Asset Container */}
                    <div className="relative aspect-16/10 w-full overflow-hidden rounded-lg bg-muted/40 border border-border/60 flex items-center justify-center p-4 group">
                      <img src={item.image} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105" />
                      <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-2xs font-semibold bg-background/90 text-foreground border border-border/80 backdrop-blur-xs">
                        {item.category}
                      </div>
                    </div>

                    {/* Spec Summary Bullets */}
                    <div className="space-y-2 pt-2 text-xs">
                      <div className="flex items-start justify-between gap-2 border-b border-border/60 pb-1.5">
                        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                          <Radio className="size-3.5 text-primary" />
                          Interface:
                        </span>
                        <span className="font-semibold text-foreground text-right">
                          {item.connectivity}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-2 border-b border-border/60 pb-1.5">
                        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                          <ShieldCheck className="size-3.5 text-primary" />
                          Ingress / Ex:
                        </span>
                        <span className="font-semibold text-foreground text-right">
                          {item.environment}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-2 border-b border-border/60 pb-1.5">
                        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                          <BatteryCharging className="size-3.5 text-primary" />
                          Power:
                        </span>
                        <span className="font-semibold text-foreground text-right">
                          {item.power}
                        </span>
                      </div>
                      <div className="flex items-start justify-between gap-2 pb-1">
                        <span className="text-muted-foreground font-medium flex items-center gap-1.5">
                          <Activity className="size-3.5 text-primary" />
                          Spectral Band:
                        </span>
                        <span className="font-semibold text-foreground text-right font-mono">
                          {item.spectralRange}
                        </span>
                      </div>
                    </div>

                    {/* Application scope */}
                    <div className="p-2.5 rounded-md bg-muted/40 text-2xs text-muted-foreground">
                      <span className="font-semibold text-foreground block mb-0.5">
                        Core Installation:
                      </span>
                      {item.primaryUse}
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 flex flex-col gap-2">
                    <div className="flex items-center gap-2 w-full">
                      <Button variant={isSelected ? "default" : "outline"} size="sm" className="w-full" asChild>
                        <Link to="/ContactPage">
                          <span>Enquire for {item.name.replace("MicroNIR ", "")}</span>
                          <ArrowRight className="size-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Detailed Side-by-Side Comparison Matrix */}
        <div className="bg-card border border-border rounded-xl p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div>
              <div className="flex items-center gap-2">
                <Settings2 className="size-4 text-primary" />
                <h3 className="text-xl font-default font-bold text-card-foreground">
                  Variant Matrix & Technical Overview
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Comparative placeholder parameters across portable, blender-mounted, and inline NIR configurations.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <Link to="/ProcessAnalyticalTechnologyPage">
                  <span>PAT Technology Guide</span>
                </Link>
              </Button>
              <Button variant="default" size="sm" asChild>
                <Link to="/ContactPage">
                  <span>Request Validation Pack</span>
                </Link>
              </Button>
            </div>
          </div>

          <Separator className="mb-6" />

          {/* Responsive Specs Table */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/40">
                  <TableHead className="w-[180px] font-bold text-foreground">
                    Parameter
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    OnSite-W
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    PAT-W
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    PAT-Wx (ATEX)
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    PAT-U
                  </TableHead>
                  <TableHead className="font-semibold text-foreground">
                    PAT-Ux (ATEX)
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {technicalMatrix.map((row, index) => <TableRow key={row.feature} data-index={index}>
                    <TableCell className="font-medium text-foreground bg-muted/20">
                      {row.feature}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.onsiteW}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.patW}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.patWx}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.patU}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {row.patUx}
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 text-2xs text-muted-foreground pt-4 border-t border-border">
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="size-3.5 text-primary" />
              <span>GAMP 5 & 21 CFR Part 11 compliance-support software packages available for all variants.</span>
            </div>
            <div className="font-mono">
              Ref: ANTECH-PAT-SPEC-2025-V1 (Review Pending)
            </div>
          </div>
        </div>
      </div>
    </section>;
}